import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getPlanification, updatePlanification, getTechniciens, demandeSuppressionWO,
         getQualification, saveQualification } from '../services/api';
import api from '../services/api';
import { aujourdhui } from '../services/dates';
import PropositionDate from '../components/PropositionDate';
import { useSettings } from '../App';

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Parse le format Zoho ERDV : "21-04-2026 13:00:00+02:00"
function parseErdv(str) {
  if (!str) return null;
  // Extrait DD-MM-YYYY HH:MM:SS
  const m = str.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})/);
  if (!m) return null;
  return { day: m[1], month: m[2], year: m[3], hour: m[4], minute: m[5] };
}
function fmtErdv(str) {
  const p = parseErdv(str);
  if (!p) return null;
  const d = new Date(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:00`);
  if (isNaN(d)) return null;
  const date = d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  // Une date E-RDV revolue n'est plus un rendez-vous : dix commandes en attente
  // sur quatorze en portaient une, toutes affichees en vert comme si elles
  // etaient encore valables. Le bloc est masque, mais la date reste accessible
  // en repli pour qui veut la verifier.
  const auj = new Date(); auj.setHours(0, 0, 0, 0);
  return { date, heure: `${p.hour}h${p.minute}`, iso: `${p.year}-${p.month}-${p.day}`, passee: d < auj };
}

function daysSince(isoDate) {
  if (!isoDate) return null;
  return Math.floor((Date.now() - new Date(isoDate).getTime()) / 86400000);
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtDateInput(isoDate) {
  if (!isoDate) return '';
  return isoDate.split('T')[0];
}
// Convertit un numéro FR (0XXXXXXXXX) en format E.164 (+33XXXXXXXXX) pour les liens tel: (Wildix)
function toE164(num) {
  const n = (num || '').replace(/\s/g, '');
  if (n.startsWith('0')) return '+33' + n.slice(1);
  return n;
}

// L'etape du parcours client. Elle decide de la file de travail, du badge et
// des actions proposees sur la ligne.
function contactStatus(itv) {
  // Une proposition en cours, c'est l'etape « Proposition RDV envoyée » pour
  // les nouvelles, ou la case cochee pour celles d'avant l'automatisation.
  //
  // La date seule ne suffisait pas : elle reste sur le dossier apres un refus,
  // et le dossier restait donc dans « En attente du client » alors qu'il n'y a
  // plus rien a attendre et qu'il faut rappeler le client.
  if (itv.stage === 'Proposition RDV envoyée' || itv.envoi_mail_rdv) return 'rdv_propose';
  if (itv.date_appel_2)  return 'appel2';
  if (itv.date_appel_1)  return 'appel1';
  return 'aucun';
}

// ─── Badge statut ─────────────────────────────────────────────────────────────
function StatutBadge({ itv }) {
  const s = contactStatus(itv);
  const cfg = {
    aucun:       { label: 'Non contacté',   bg: '#fee2e2', color: '#b91c1c' },
    appel1:      { label: '1er appel fait', bg: '#fef3c7', color: '#b45309' },
    appel2:      { label: '2ème appel fait',bg: '#ffedd5', color: '#c2410c' },
    rdv_propose: { label: 'RDV proposé',    bg: '#dbeafe', color: '#1d4ed8' },
  }[s];
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

// ─── Secteur ─────────────────────────────────────────────────────────────────
function getSecteur(cp) {
  if (!cp) return null;
  const s = String(cp).trim();
  const d2 = s.slice(0, 2);
  const d1 = s.slice(0, 1);
  const secA2 = ['75','92','91','77','94','69','42','38','07','43','03','63','01','26'];
  const secB2 = ['78','93','95','13','06','83','74','73','30','84'];
  if (secA2.includes(d2)) return 'A';
  if (secB2.includes(d2)) return 'B';
  if (d1 === '7') return 'A';
  if (d1 === '3') return 'A';
  if (d1 === '1') return 'A';
  if (d1 === '6') return 'B';
  return null;
}

const SAV_TYPES = ['SAV','RACC suite SAV (RAC_I)','RACC suite SAV (RAC_C)','RACC suite SAV (RAC_P)','RACC suite SAV (RAC_F)'];

const TYPO_COLORS = {
  'RAC_I': { color: '#185FA5', bg: 'rgba(24,95,165,0.12)' },
  'RAC_C': { color: '#185FA5', bg: 'rgba(24,95,165,0.12)' },
  'RAC_F': { color: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
  'RAC_P': { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
};
function TypoBadge({ typo }) {
  if (!typo) return null;
  const cfg = TYPO_COLORS[typo] || { color: '#8b92a5', bg: 'rgba(139,146,165,0.15)' };
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 10, color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap' }}>{typo}</span>;
}

// ─── Lecture du parcours ──────────────────────────────────────
/**
 * Le délai qui compte est celui du work order, pas celui de la commande.
 *
 * Une commande revient sous la même référence O avec un W neuf : O250902_14451
 * affiche 267 jours sur la commande et zéro sur le W arrivé le 9 octobre.
 * Compter la commande ferait courir la conduite d’activité après des dossiers
 * qui ne sont pas en retard.
 *
 * Le seuil est celui que Covage mesure — I001/I002, deux jours ouvrés entre
 * l’arrivée du W et la proposition de RDV. Il est calculé côté serveur par le
 * calendrier des KPI et arrive dans `jours_ouvres_w`.
 */
const SEUIL_OUVRE = 2;

/**
 * Une commande qui vient d’arriver et que personne n’a encore touchée.
 *
 * Deux conditions, pas une : le work order est arrivé aujourd’hui — zéro jour
 * ouvré d’ancienneté — et aucun appel n’est tracé. Le badge disparaît donc dès
 * le premier appel, ou le lendemain matin, au premier des deux. Sur la seule
 * ancienneté il resterait sur un dossier qu’on vient de traiter ; sur le seul
 * contact il finirait par annoncer « nouveau » un dossier vieux d’une semaine.
 */
function toutJuste(itv) {
  return itv.jours_ouvres_w === 0 && contactStatus(itv) === 'aucun';
}

function horsSeuil(itv) {
  const n = itv.jours_ouvres_w;
  return contactStatus(itv) !== 'rdv_propose' && n != null && n > SEUIL_OUVRE;
}
function dernierJour(itv) {
  const n = itv.jours_ouvres_w;
  return contactStatus(itv) !== 'rdv_propose' && n === SEUIL_OUVRE;
}
// Un créneau E-RDV déjà passé ne porte plus rien : la proposition ne peut pas
// s’y adosser. Quatorze des seize créneaux posés étaient dans ce cas.
function erdvPerime(itv) { const e = fmtErdv(itv.date_erdv); return !!(e && e.passee); }
function erdvPose(itv)   { return !!fmtErdv(itv.date_erdv); }

const ETAPES_PARCOURS = [
  { k: 'aucun',       rang: 'Étape 1', nom: 'Non contacté',          quoi: 'Le W est arrivé, personne n’a encore appelé' },
  { k: 'appel1',      rang: 'Étape 2', nom: '1er appel sans succès', quoi: 'À relancer : le 2e appel ouvre la suite' },
  { k: 'appel2',      rang: 'Étape 3', nom: '2e appel sans succès',  quoi: 'Les deux appels sont tracés, la suppression du WO devient possible' },
  { k: 'rdv_propose', rang: 'Étape 4', nom: 'Proposition envoyée',   quoi: 'En attente de la réponse du client' },
];

const FILES_ATTENTE = [
  { k: 'aucun',       h: 'À appeler',                 dit: 'Premier contact. Marquer le 1er appel sans succès le date et le trace.' },
  { k: 'appel1',      h: 'Relancer',                  dit: 'Un seul appel tracé. Le 2e appel conditionne la demande de suppression.' },
  { k: 'appel2',      h: 'Deux appels sans réponse',  dit: 'Les seuls dossiers pour lesquels la suppression du WO peut être demandée à Covage.' },
  { k: 'rdv_propose', h: 'En attente du client',      dit: 'La proposition est partie. Relancer avant que le créneau proposé ne passe.' },
];

// ─── Âge du work order ───────────────────────────────────────
function AgeW({ itv }) {
  const n = itv.jours_ouvres_w;
  const propose = contactStatus(itv) === 'rdv_propose';
  const jCmd = daysSince(itv.date_reception);
  const rappelCommande = jCmd != null && jCmd > 60
    ? <div style={{ fontSize: 10, color: '#b45309', marginTop: 3, lineHeight: 1.3 }}>commande ouverte depuis {jCmd} j</div>
    : null;

  // Notre historique d’étapes a des trous : sans date d’entrée en « A planifier »,
  // on ne peut rien affirmer sur le délai, et surtout pas un dépassement.
  if (n == null) {
    return (
      <div>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>arrivée du W inconnue</span>
        {rappelCommande}
      </div>
    );
  }

  const hors = horsSeuil(itv), der = dernierJour(itv);
  const couleur = hors ? '#b91c1c' : der ? '#b45309' : 'var(--text)';
  const part = Math.round(Math.max(0.1, Math.min(1, n / SEUIL_OUVRE)) * 100);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 17, fontWeight: 800, color: couleur, letterSpacing: '-.02em' }}>{n}</span>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>j ouvré{n > 1 ? 's' : ''}</span>
      </div>
      {!propose && (
        <div style={{ height: 3, borderRadius: 2, background: 'var(--border)', marginTop: 5, maxWidth: 76, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: part + '%', background: hors ? '#b91c1c' : der ? '#b45309' : '#15803d' }} />
        </div>
      )}
      {rappelCommande}
    </div>
  );
}

// ─── Parcours client ─────────────────────────────────────────
function Jalon({ fait, label, quand, bleu }) {
  const teinte = bleu ? '#1d4ed8' : '#15803d';
  return (
    <div style={{ minWidth: 60 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, height: 14 }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          background: fait ? teinte : 'var(--bg3)',
          border: `1.5px solid ${fait ? teinte : 'var(--border2)'}`,
        }} />
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.02em', whiteSpace: 'nowrap',
          color: fait ? 'var(--text)' : 'var(--text3)' }}>{label}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', paddingLeft: 13, lineHeight: 1.35 }}>{quand || '—'}</div>
    </div>
  );
}

function Parcours({ itv }) {
  const court = iso => iso
    ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '')
    : null;
  const propose = contactStatus(itv) === 'rdv_propose';
  const jMail = itv.jours_ouvres_mail;

  let dit = null;
  if (propose) {
    const d = itv.date_proposition_rdv
      ? new Date(itv.date_proposition_rdv).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
      : '—';
    // Quand le mail est parti compte autant que la date proposée : c’est lui qui
    // dit depuis combien de temps le client n’a pas répondu.
    const envoi = jMail == null ? 'envoi non tracé'
      : jMail === 0 ? 'mail parti aujourd’hui'
      : `mail parti il y a ${jMail} j ouvré${jMail > 1 ? 's' : ''}`;
    dit = `${d} · ${envoi}`;
  }

  const barre = ok => (
    <div style={{ height: 1.5, flex: 1, minWidth: 10, margin: '6.5px 3px 0', background: ok ? '#15803d' : 'var(--border)' }} />
  );

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      <Jalon fait={!!itv.date_appel_1} label="1er appel" quand={court(itv.date_appel_1)} />
      {barre(!!itv.date_appel_2)}
      <Jalon fait={!!itv.date_appel_2} label="2e appel" quand={court(itv.date_appel_2)} />
      {barre(propose)}
      <div style={{ minWidth: 88 }}>
        <Jalon fait={propose} label="Proposé" quand={dit} bleu />
      </div>
    </div>
  );
}

// D'où vient le rendez-vous, croisé entre la grille et notre miroir. Une
// réservation ferme le créneau dans Zoho : « fermé » tout seul ne distingue
// donc pas un client qui a réservé d'un créneau que nous n'avions jamais
// ouvert, et c'est pourtant toute la différence.
const ORIGINE = {
  reserve: 'Réservé par le client sur notre grille — le moteur avait ouvert ce créneau',
  attend: 'Créneau ouvert dans notre grille, pas encore réservé',
  hors_grille: 'Créneau que nous n’avions pas ouvert : ce rendez-vous ne vient pas de notre grille',
};

// ─── Créneau Covage ─────────────────────────────────────────
function BadgeErdv({ itv }) {
  const e = fmtErdv(itv.date_erdv);
  const base = {
    fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap',
  };
  const pt = c => <span style={{ width: 6, height: 6, borderRadius: '50%', background: c, flexShrink: 0, opacity: .85 }} />;

  if (!e) return (
    <span style={{ ...base, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text3)' }}>
      {pt('currentColor')}Aucun créneau
    </span>
  );
  const court = e.iso.split('-').reverse().slice(0, 2).join('/');
  if (e.passee) return (
    <span title={`Créneau Covage dépassé : ${e.date} à ${e.heure}`}
      style={{ ...base, background: '#fee2e2', border: '1px solid #fecaca', color: '#b91c1c' }}>
      {pt('#b91c1c')}Périmé {court}
    </span>
  );
  // À qui ce créneau est destiné. La commande n'arrive qu'avec une date : sans
  // cela on place au jugement. C'est aussi le contrôle du moteur — un créneau
  // ouvert chez nous avec un technicien libre dit qu'il a vu juste, un créneau
  // fermé et pourtant réservé dit que le rendez-vous n'est pas venu de notre
  // grille, ce qui arrive puisque la date voyage avec la commande.
  const p = itv.erdv_pour;
  const pour = p && !p.passe ? p : null;
  const bon = pour && pour.techniciens.length > 0;
  // Un seul nom, celui de l'appariement : afficher les cinq techniciens libres
  // laissait choisir celui qui prive le rendez-vous voisin.
  const choisi = bon ? (pour.recommande || pour.techniciens[0]) : null;
  const autres = bon ? pour.techniciens.filter(t => t !== choisi) : [];

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span title={`${e.date} à ${e.heure}`}
        style={{ ...base, background: '#dcfce7', border: '1px solid #86efac', color: '#15803d' }}>
        {pt('#15803d')}E-RDV {court}
      </span>
      {pour && (
        <span
          title={[
            `${pour.dept} — ${pour.date} ${pour.creneau}`,
            ORIGINE[pour.origine] || null,
            bon ? 'À placer sur : ' + (pour.recommande || pour.techniciens[0])
              : 'Aucun technicien disponible sur ce créneau',
            // Les autres sont libres aussi, mais les prendre peut priver un
            // rendez-vous voisin : c'est ce qui a fermé le 78 du 29 octobre.
            bon && autres.length
              ? 'Aussi libres, mais à éviter : ' + autres.join(', ')
              : null,
            pour.concurrents
              ? `${pour.concurrents} autre(s) rendez-vous sur ce créneau se disputent les mêmes techniciens`
              : null,
            ...(pour.ecartes || []),
          ].filter(Boolean).join('\n')}
          style={{
            ...base,
            background: bon ? '#e0e7ff' : '#fef3c7',
            border: '1px solid ' + (bon ? '#a5b4fc' : '#fcd34d'),
            color: bon ? '#3730a3' : '#92400e',
          }}>
          {pt(bon ? '#3730a3' : '#92400e')}
          {bon ? choisi : 'aucun technicien'}
          {bon && autres.length > 0 && (
            <span style={{ fontWeight: 500, opacity: .7 }}>+{autres.length}</span>
          )}
        </span>
      )}
    </span>
  );
}

// ─── Passages précédents ───────────────────────────────────────
/**
 * Combien de fois ce dossier est déjà passé.
 *
 * Une commande neuve se planifie directement. La neuvième tentative sur la
 * même adresse demande de lire d’abord pourquoi les huit précédentes ont
 * échoué — une chambre encombrée, un percement refusé, un PBO saturation.
 * L’information était dans la fiche, une commande à la fois.
 *
 * Les deux comptes sont des minorants : notre historique d’étapes a des trous,
 * et un W peut arriver puis repartir sans intervention.
 */
function Passages({ itv }) {
  const n = itv.nb_wo_precedents;
  const crs = itv.nb_cr_rendus;

  if (!n) {
    // Zero passage connu ne veut pas dire zero passage. O250902_14451 affichait
    // « 1re commande » alors que son journal raconte un an de tentatives : la
    // commande est de septembre 2025, le W d'octobre 2026, et les W
    // intermediaires manquent a notre historique. L'ecart entre l'ouverture de
    // la commande et l'arrivee du W le dit — une commande neuve recoit son
    // premier W dans les jours qui suivent.
    const ecart = (itv.date_reception && itv.date_debut_w)
      ? Math.round((new Date(itv.date_debut_w) - new Date(itv.date_reception)) / 86400000)
      : null;

    if (ecart != null && ecart > 20) {
      const ouverture = itv.date_reception.split('-').reverse().join('/');
      return (
        <div>
          <span title={`Commande ouverte le ${ouverture}, work order arrive ${ecart} jours plus tard : `
            + 'des work orders precedents manquent a notre historique.'}
            style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 5, whiteSpace: 'nowrap',
              background: '#fef3c7', border: '1px solid #fde68a', color: '#b45309' }}>
            Historique incomplet
          </span>
          <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3, lineHeight: 1.3 }}>
            commande du {ouverture} — lire le journal
          </div>
        </div>
      );
    }

    return (
      <div>
        <span style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 5, whiteSpace: 'nowrap',
          background: '#dcfce7', border: '1px solid #86efac', color: '#15803d' }}>
          1re commande
        </span>
        <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3, lineHeight: 1.3 }}>
          planification directe
        </div>
      </div>
    );
  }

  // Au-delà de deux passages, le dossier résiste : c’est le signal, pas le compte.
  const dur = n >= 3;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.02em',
          color: dur ? '#b45309' : 'var(--text)' }}>{n}</span>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>passage{n > 1 ? 's' : ''}</span>
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 2, lineHeight: 1.3 }}>
        {crs} CR rendu{crs > 1 ? 's' : ''}
        {itv.dernier_passage && <> · {itv.dernier_passage.split('-').reverse().slice(0, 2).join('/')}</>}
      </div>
      {itv.dernier_echec && (
        <div title={itv.dernier_echec} style={{ fontSize: 10.5, color: '#b45309', marginTop: 2, lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {itv.dernier_echec}
        </div>
      )}
    </div>
  );
}

// ─── Signaux de préparation ────────────────────────────────────
// Une nacelle se réserve, un arrêt de circulation se demande à la voirie, un
// devis attend un accord : trois raisons de ne pas planifier pour demain. Elles
// se lisaient une commande à la fois sur la fiche.
const SIGNAUX = [
  { champ: 'devis_forfaitaire', lbl: 'Devis',  fond: '#f3e8ff', bord: '#e9d5ff', texte: '#7e22ce', titre: 'Devis forfaitaire — accord à obtenir avant de planifier' },
  { champ: 'nacelle',           lbl: 'Nacelle', fond: '#e0f2fe', bord: '#bae6fd', texte: '#0369a1', titre: 'Nacelle à prévoir — matériel à réserver' },
  { champ: 'arc_necessaire',    lbl: 'ARC',    fond: '#fef3c7', bord: '#fde68a', texte: '#b45309', titre: 'Arrêt de circulation nécessaire — délai de voirie' },
  { champ: 'dostec',            lbl: 'DOSTEC', fond: '#fee2e2', bord: '#fecaca', texte: '#b91c1c', titre: 'DOSTEC — dossier technique à monter' },
];

function Signaux({ itv }) {
  const actifs = SIGNAUX.filter(s => itv[s.champ]);
  if (!actifs.length) return null;
  return (
    <>
      {actifs.map(s => (
        <span key={s.champ} title={s.titre} style={{
          fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap',
          background: s.fond, border: `1px solid ${s.bord}`, color: s.texte,
        }}>{s.lbl}</span>
      ))}
    </>
  );
}

// ─── Ligne de file ───────────────────────────────────────────
// La colonne des passages s'insère avant l'âge du W : on lit d'abord si le
// dossier est neuf, ensuite depuis quand il attend.
// Une couleur par section, choisie sur ce qu'elle contient : ce qui informe en
// bleu, ce qu'on ecrit en rouge VALEA, les tentatives en ambre, ce qui engage
// le client en vert. Les memes teintes que les pastilles des lignes.
const TEINTES = {
  contact:      '#3b82f6',
  journal:      '#D42B2B',
  commentaires: '#a855f7',
  appels:       '#d97706',
  proposition:  '#2563eb',
  confirmation: '#16a34a',
  covage:       '#0891b2',
  etape:        '#b91c1c',
};

const COLONNES = '146px minmax(170px,1fr) 100px 128px 108px 206px 140px 158px';

function LigneQueue({ itv, actif, enCours, onOuvrir, onRelancer, onAppel, onSupprWO }) {
  const etat = contactStatus(itv);
  const secteur = getSecteur(itv.code_postal);
  const occupe = enCours === itv.id;

  const bouton = (libelle, style, action) => (
    <button
      disabled={occupe}
      onClick={e => { e.stopPropagation(); action(); }}
      style={{
        fontSize: 12, fontWeight: 700, padding: '6px 11px', borderRadius: 7,
        cursor: occupe ? 'default' : 'pointer', whiteSpace: 'nowrap', opacity: occupe ? .5 : 1, ...style,
      }}
    >{occupe ? '⏳…' : libelle}</button>
  );
  const primaire   = { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' };
  const secondaire = { background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border2)' };
  const danger     = { background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' };

  return (
    <div
      onClick={() => onOuvrir(itv)}
      style={{
        display: 'grid', gridTemplateColumns: COLONNES, gap: 14, alignItems: 'center',
        padding: '11px 16px', borderTop: '1px solid var(--border)', cursor: 'pointer',
        background: actif ? 'rgba(24,95,165,0.07)' : 'transparent',
      }}
      onMouseEnter={e => { if (!actif) e.currentTarget.style.background = 'var(--bg3)'; }}
      onMouseLeave={e => { if (!actif) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--accent)' }}>
            {itv.ref_commande || '—'}
          </span>
          {toutJuste(itv) && (
            <span title="Work order arrivé aujourd’hui, aucun appel tracé"
              style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.06em', padding: '1px 5px',
                borderRadius: 4, background: '#16a34a', color: '#fff', flexShrink: 0 }}>NEW</span>
          )}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--text3)', marginTop: 1 }}>
          {itv.ref_work_order || '—'}
        </span>
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          <span title={itv.client_final} style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '-.01em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itv.client_final || '—'}</span>
          <Signaux itv={itv} />
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {[itv.ville, itv.code_postal].filter(Boolean).join(' · ')}
          {secteur && <> · secteur {secteur}</>}
          {itv.client_operateur && <> · {itv.client_operateur}</>}
        </div>
      </div>

      <div>
        {SAV_TYPES.includes(itv.typo_raccordement)
          ? <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 5, background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}>SAV</span>
          : <TypoBadge typo={itv.typo_raccordement} />}
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
          {itv.complexite
            ? <strong style={{ color: 'var(--text)' }}>{itv.complexite}</strong>
            : <span style={{ color: '#b91c1c', fontWeight: 700 }}>CX vide</span>}
          {itv.priorite && <> · <strong style={{ color: 'var(--text)' }}>{itv.priorite}</strong></>}
        </div>
      </div>

      <Passages itv={itv} />
      <AgeW itv={itv} />
      <Parcours itv={itv} />
      <div><BadgeErdv itv={itv} /></div>

      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {etat === 'aucun'  && bouton('1er appel', primaire, () => onAppel(itv, 'appel1'))}
        {etat === 'appel1' && bouton('2e appel',  primaire, () => onAppel(itv, 'appel2'))}
        {etat === 'appel2' && bouton('Suppression WO', danger, () => onSupprWO(itv))}
        {etat === 'rdv_propose' && bouton('Relancer', secondaire, () => onRelancer(itv))}
        <a href={`/dossier/${itv.id}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
          style={{ fontSize: 12, fontWeight: 700, padding: '6px 11px', borderRadius: 7, textDecoration: 'none',
            background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border2)', whiteSpace: 'nowrap' }}>
          Fiche ↗
        </a>
      </div>
    </div>
  );
}

// ─── Une file de travail ─────────────────────────────────────
const ENTETES = ['Références', 'Client', 'Typologie', 'Passages', 'Âge du W', 'Parcours client', 'Créneau Covage', ''];

function FileQueue({ file, lignes, selected, enCours, onOuvrir, onRelancer, onAppel, onSupprWO }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '0 2px 8px', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-.01em', margin: 0 }}>{file.h}</h2>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', background: 'var(--bg2)',
          border: '1px solid var(--border)', borderRadius: 20, padding: '1px 9px' }}>{lignes.length}</span>
        <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto', textAlign: 'right' }}>{file.dit}</span>
      </div>

      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {lignes.length === 0 ? (
          <div style={{ padding: '20px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ width: 26, height: 26, borderRadius: '50%', border: '1.5px dashed var(--border2)', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Personne ici</div>
              <div style={{ fontSize: 12.5, color: 'var(--text3)', maxWidth: '64ch', lineHeight: 1.5 }}>{file.dit}</div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: COLONNES, gap: 14, padding: '8px 16px',
              background: 'var(--bg3)', borderBottom: '1px solid var(--border)' }}>
              {ENTETES.map((h, i) => (
                <span key={i} style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.07em',
                  textTransform: 'uppercase', color: 'var(--text3)' }}>{h}</span>
              ))}
            </div>
            {lignes.map(itv => (
              <LigneQueue
                key={itv.id}
                itv={itv}
                actif={selected === itv.id}
                enCours={enCours}
                onOuvrir={onOuvrir}
                onRelancer={onRelancer}
                onAppel={onAppel}
                onSupprWO={onSupprWO}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Info row ─────────────────────────────────────────────────────────────────
function InfoRow({ label, value, link, mono }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
      <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, minWidth: 140, flexShrink: 0 }}>{label}</span>
      {link
        ? <a href={link} style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, textDecoration: 'none', fontFamily: mono ? 'var(--mono)' : 'inherit' }}>{value}</a>
        : <span style={{ fontSize: 12, color: 'var(--text)', fontFamily: mono ? 'var(--mono)' : 'inherit', wordBreak: 'break-word' }}>{value}</span>}
    </div>
  );
}

/**
 * Une section du volet, qui dit repliée ce qu’elle contient.
 *
 * Les cadres se repliaient sans rien annoncer : il fallait ouvrir « Support
 * Covage » pour découvrir s’il y avait un nom dedans, et ouvrir « Tentatives
 * de contact » pour savoir si un appel était tracé. Le résumé porte la
 * réponse, et on n’ouvre plus que pour agir.
 */
function Section({ titre, resume, couleur, ouvertParDefaut = false, forcerOuvert = 0, enfants, children }) {
  const [ouvert, setOuvert] = useState(ouvertParDefaut);
  const contenu = children !== undefined ? children : enfants;

  // « Trouver une date auto » remplit la proposition et la confirmation : il
  // faut que les deux s'ouvrent, sinon le remplissage se fait sans qu'on le
  // voie et on croit que le bouton n'a rien fait.
  useEffect(() => { if (forcerOuvert) setOuvert(true); }, [forcerOuvert]);
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)',
      borderLeft: `3px solid ${couleur || 'var(--border2)'}`, borderRadius: 10, overflow: 'hidden' }}>
      <button
        onClick={() => setOuvert(o => !o)}
        aria-expanded={ouvert}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '10px 14px',
          background: 'transparent', border: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit',
          color: 'inherit', borderBottom: ouvert ? '1px solid var(--border)' : 'none',
        }}
      >
        <span style={{ fontSize: 10, color: couleur || 'var(--text3)', flexShrink: 0 }}>{ouvert ? '▾' : '▸'}</span>
        {/* Le titre porte la couleur de la section : huit en-tetes du meme
            gris que le texte, c'etait huit en-tetes qu'il fallait chercher. */}
        <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase',
          color: couleur || 'var(--text2)', whiteSpace: 'nowrap' }}>{titre}</span>
        {!ouvert && resume && (
          <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto', textAlign: 'right',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resume}</span>
        )}
      </button>
      {ouvert && <div style={{ padding: '12px 14px' }}>{contenu}</div>}
    </div>
  );
}

// ─── Editable contact row ─────────────────────────────────────────────────────
function EditableContactRow({ label, value, onChange, type, link, mono, saving, onSave }) {
  const [editing, setEditing] = React.useState(false);
  return (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
      <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, minWidth: 140, flexShrink: 0 }}>{label}</span>
      {editing ? (
        <>
          <input
            autoFocus
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{ flex: 1, fontSize: 12, padding: '2px 6px', border: '1px solid var(--accent)', borderRadius: 4, fontFamily: mono ? 'var(--mono)' : 'inherit', minWidth: 0 }}
          />
          <button onClick={() => { onSave(); setEditing(false); }} disabled={saving} style={{ fontSize: 11, padding: '2px 8px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}>✓</button>
          <button onClick={() => setEditing(false)} style={{ fontSize: 11, padding: '2px 6px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </>
      ) : (
        <>
          <span style={{ flex: 1, fontSize: 12, minWidth: 0 }}>
            {value
              ? (link
                  ? <a href={link} style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none', fontFamily: mono ? 'var(--mono)' : 'inherit' }}>{value}</a>
                  : <span style={{ color: 'var(--text)', fontFamily: mono ? 'var(--mono)' : 'inherit', wordBreak: 'break-word' }}>{value}</span>)
              : <span style={{ color: 'var(--text3)' }}>—</span>}
          </span>
          <button onClick={() => setEditing(true)} title="Modifier" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: '0 2px', flexShrink: 0, lineHeight: 1 }}>✏️</button>
        </>
      )}
    </div>
  );
}

// ─── Panel détail ─────────────────────────────────────────────────────────────
// Actions qui retirent le dossier de la liste "A planifier"
const ACTIONS_REMOVE = ['confirmer', 'changer_stage'];

/**
 * Qualification du dossier au regard des SLA.
 *
 * Covage accepte de sortir du calcul les ecarts dont le client est a
 * l'origine, mais rien dans les donnees ne permet de les reconnaitre : un
 * rendez-vous decale a la demande du client ressemble en tout point a un
 * rendez-vous que nous n'avons pas tenu. Seule la personne qui a eu le client
 * au telephone le sait.
 *
 * Jennifer a confirme le 9 octobre 2026 que notre declaration suffit, sans
 * justificatif. Une date AVANCEE, elle, ne se declare pas : le moteur la
 * reconnait seul, la date realisee etant anterieure a la date E-RDV.
 */
// Les trois motifs que Covage admet. Le refus de CR conteste manquait ici :
// il n'existait que dans la fiche, alors que c'est depuis cette file que la
// conduite d'activite travaille.
const VIDE = { erdv_client: false, delai_client: false, cr_conteste: false, commentaire: '' };

function QualificationSLA({ itv }) {
  const [etat, setEtat] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [enregistre, setEnregistre] = useState(false);
  const [erreur, setErreur] = useState(null);

  const dossierId = itv.id;
  const refWO = itv.ref_work_order || '';

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    getQualification({ dossier: dossierId, wo: refWO })
      .then(d => { if (vivant) setEtat(d.item || VIDE); })
      .catch(() => { if (vivant) setEtat(VIDE); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [dossierId, refWO]);

  const basculer = async (champ) => {
    const suivant = { ...etat, [champ]: !etat[champ] };
    setEtat(suivant);
    setErreur(null);
    try {
      const r = await saveQualification({
        dossierId, refWorkOrder: refWO,
        erdv_client: suivant.erdv_client,
        delai_client: suivant.delai_client,
        cr_conteste: suivant.cr_conteste,
        commentaire: suivant.commentaire || '',
      });
      setEtat(r.item || VIDE);
      setEnregistre(true);
      setTimeout(() => setEnregistre(false), 2000);
    } catch (e) {
      setEtat(etat);
      setErreur(e.response?.data?.error || 'Enregistrement impossible');
    }
  };

  if (chargement || !etat) return null;

  const coche = (champ, titre, aide) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer', padding: '6px 0' }}>
      <input type="checkbox" checked={!!etat[champ]} onChange={() => basculer(champ)}
        style={{ width: 17, height: 17, marginTop: 1, accentColor: '#b45309', flexShrink: 0 }} />
      <span>
        <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{titre}</span>
        <span style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.45 }}>{aide}</span>
      </span>
    </label>
  );

  const actif = etat.erdv_client || etat.delai_client || etat.cr_conteste;

  return (
    <div style={{
      marginTop: 10, padding: '11px 14px', borderRadius: 10,
      background: actif ? '#fffbeb' : 'var(--bg2)',
      border: '1px solid ' + (actif ? '#fde68a' : 'var(--border)'),
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: actif ? '#b45309' : 'var(--text3)' }}>
          Sortie du calcul SLA
        </span>
        {enregistre && <span style={{ fontSize: 11, color: 'var(--green2)', fontWeight: 600 }}>enregistre</span>}
        {erreur && <span style={{ fontSize: 11, color: '#b91c1c' }}>{erreur}</span>}
      </div>
      {coche('erdv_client', 'E-RDV non respecté suite demande client',
        'Le client a lui-même demandé à changer la date. Le dossier quitte le dénominateur du taux E-RDV.')}
      {coche('delai_client', 'Délai de planification allongé à la demande du client',
        'Le client a souhaité une date plus lointaine. Le dossier sort des quatre engagements de '
        + 'délai : planification 2 jours ouvrés, première intervention 15 jours ouvrés, '
        + 'PLP 10 jours ouvrés et production globale 25 jours.')}
      {coche('cr_conteste', 'Refus de CR contesté — le compte rendu était conforme',
        'Covage a basculé le dossier en « CR non conforme » à tort. Le refus reste dans l’historique, '
        + 'donc le taux le retient : le contester le fait compter comme conforme.')}
      {etat.par && (
        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
          déclaré par {etat.par} le {new Date(etat.le).toLocaleString('fr-FR')}
        </div>
      )}
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6, lineHeight: 1.5 }}>
        Une date <strong>avancée</strong> n’a pas à être déclarée : elle est reconnue automatiquement.
        La revue d’ensemble est dans « Qualifications SLA ».
      </div>
    </div>
  );
}

// ─── Lectures utiles au volet ───────────────────────────────────
// L’import Covage casse les accents : « HervÃ© » pour « Hervé ». La fiche sait
// déjà les réparer ; le volet lit les mêmes champs, il répare pareil.
function reparerAccents(t) {
  const v = String(t || '');
  if (!/[ÃÂ]/.test(v)) return v;
  try { return decodeURIComponent(escape(v)); } catch { return v; }
}

const RE_TEL  = /(?:\+33[\s.]?|0)[\s.]?\d(?:[\s.]?\d{2}){4}/g;
const RE_MAIL = /[\w.+-]+@[\w-]+\.[\w.]{2,}/g;

/** Un numéro réduit à ses dix chiffres, pour pouvoir le comparer. */
function numeroNu(t) {
  const x = String(t || '').replace(/[\s.()–-]/g, '');
  return x.startsWith('+33') ? '0' + x.slice(3) : x;
}

/**
 * Les numéros qui vivent dans les commentaires sans être dans les champs.
 *
 * O250902_14451 porte « mobile a transmettre 0676267504 » dans son journal et
 * un seconde ligne fixe dans le commentaire VALEA : ni l’un ni l’autre n’est
 * dans la fiche, et la conduite d’activité les relisait à chaque appel.
 */
function telsCaches(itv) {
  const connus = new Set([itv.tel_fixe, itv.tel_mobile, itv.tel_support_kosc]
    .filter(Boolean).map(numeroNu));
  const texte = [itv.commentaire_planif, itv.commentaire_valea, itv.commentaire_interne,
                 itv.commentaire_client, itv.commentaire_covage]
    .filter(Boolean).map(reparerAccents).join(' ');
  const vus = new Map();
  for (const brut of (texte.replace(RE_MAIL, ' ').match(RE_TEL) || [])) {
    const n = numeroNu(brut);
    if (n.length !== 10 || connus.has(n) || vus.has(n)) continue;
    vus.set(n, brut.trim());
  }
  return [...vus.keys()].map(n => ({ numero: n, affiche: n.replace(/(\d{2})(?=\d)/g, '$1 ') }));
}

// Les lignes du journal commencent par une date tapée à la main, dans trois
// formats au moins — « 02/09/25 », « 04/12 », et jusqu’à « 04//09/25 ». On les
// lit toutes ; une ligne sans date reste affichée, la perdre serait pire.
const RE_DATE_JOURNAL = /^\s*(\d{1,2}\s*\/\/?\s*\d{1,2}(?:\s*\/\s*\d{2,4})?)\s*:?\s*/;

function entreesJournal(brut) {
  return String(brut || '').split(/\r?\n/).filter(l => l.trim()).map(ligne => {
    const m = ligne.match(RE_DATE_JOURNAL);
    return m
      ? { quand: m[1].replace(/\s/g, '').replace('//', '/'), quoi: ligne.slice(m[0].length) }
      : { quand: null, quoi: ligne.trim() };
  });
}

/** Ce que la ligne raconte, pour qu’on le voie sans lire. */
function etiquetteJournal(quoi) {
  const t = String(quoi).toLowerCase();
  if (/supp/.test(t))                return ['suppression WO', '#ff6b6b', 'rgba(212,43,43,.16)'];
  if (/refuse/.test(t))              return ['refus client', '#ff6b6b', 'rgba(212,43,43,.16)'];
  if (/rdv valid|validé/.test(t))  return ['RDV validé', '#15803d', 'rgba(22,163,74,.16)'];
  if (/proposition|mail/.test(t))    return ['proposition', '#1d4ed8', 'rgba(37,99,235,.16)'];
  if (/appel|injoignable/.test(t))   return ['appel', '#b45309', 'rgba(217,119,6,.16)'];
  return null;
}

const COURT = iso => iso
  ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '')
  : null;

const JJMM = iso => String(iso || '').slice(0, 10).split('-').reverse().slice(0, 2).join('/');

// ─── Le volet ──────────────────────────────────────────────
/**
 * Le dossier ouvert à côté de la file.
 *
 * Douze cadres se suivaient dans le même gris, tous refermés sur leur contenu.
 * Le volet dit maintenant, dans l’ordre : qui c’est, ce qu’il faut faire
 * maintenant, les quatre faits qui décident, puis qui appeler, ce qu’on sait du
 * dossier, et enfin planifier et l’administratif. Chaque section repliée porte
 * son résumé.
 *
 * Rien n’a été retiré : les mêmes actions écrivent dans les mêmes champs.
 */
function DetailPanel({ itv, techniciens, onRefresh, onRemove }) {
  const [saving, setSaving]           = useState('');
  const [msg, setMsg]                 = useState('');
  const [suppDone, setSuppDone]       = useState(false);
  const [suppLoading, setSuppLoading] = useState(false);
  const [datePropo, setDatePropo]     = useState('');
  const [heurePropo, setHeurePropo]   = useState('');
  const [envoiMail, setEnvoiMail]     = useState(false);
  const [dateRDV, setDateRDV]         = useState('');
  const [heureRDV, setHeureRDV]       = useState('');
  const [techRDV, setTechRDV]         = useState('');
  const [ligneJournal, setLigneJournal] = useState('');
  const [commentaireValea, setCommentaireValea]     = useState('');
  const [commentaireInterne, setCommentaireInterne] = useState('');
  const [telFixe, setTelFixe]         = useState('');
  const [telMobile, setTelMobile]     = useState('');
  // Incremente a chaque creneau choisi : il ouvre la proposition et la
  // confirmation, qui viennent d'etre remplies.
  const [creneauChoisi, setCreneauChoisi] = useState(0);
  const [mailContact, setMailContact] = useState('');
  const [mailContact2, setMailContact2] = useState('');

  useEffect(() => {
    setMsg('');
    setSuppDone(false);
    setDatePropo(fmtDateInput(itv.date_proposition_rdv));
    setHeurePropo(itv.date_proposition_rdv ? new Date(itv.date_proposition_rdv).toTimeString().slice(0, 5) : '');
    setEnvoiMail(itv.envoi_mail_rdv || false);
    setDateRDV(itv.date_intervention || '');
    setHeureRDV(itv.heure_intervention || '');
    setTechRDV(itv.technicien || '');
    setLigneJournal('');
    setCommentaireValea(reparerAccents(itv.commentaire_valea || ''));
    setCommentaireInterne(itv.commentaire_interne || '');
    setTelFixe(itv.tel_fixe || '');
    setTelMobile(itv.tel_mobile || '');
    setMailContact(itv.mail_contact || '');
    setMailContact2(itv.mail_contact_2 || '');
  }, [itv.id]); // eslint-disable-line

  const doAction = async (action, data = {}) => {
    setSaving(action); setMsg('');
    try {
      await updatePlanification(itv.id, { action, dossier_ref: itv.ref_commande, ...data });
      setMsg('✓ Enregistré dans Zoho');
      if (ACTIONS_REMOVE.includes(action)) onRemove(itv.id);
      setTimeout(() => onRefresh(), 1500);
    } catch (e) {
      // Poser ce technicien peut retirer le dernier qui pouvait tenir un
      // creneau deja reserve ailleurs. Le serveur refuse une premiere fois,
      // nomme ce qui serait perdu, et n'ecrit que si on le veut quand meme.
      const rep = e.response?.data;
      // Un horaire hors grille Covage — une intervention de nuit — ne bloque
      // rien : on le dit, et on envoie si la conduite d'activite le veut.
      if (rep?.hors_grille) {
        setSaving('');
        if (window.confirm(rep.error + '\n\nEnvoyer la proposition quand même ?')) {
          return doAction(action, { ...data, confirme: true });
        }
        setMsg('✗ Proposition annulée');
        return;
      }
      if (rep?.confirmation_requise) {
        setSaving('');
        const lignes = (rep.orphelins || []).map(o =>
          `— ${o.dept}, le ${String(o.date).split('-').reverse().join('/')} à ${o.creneau} :`
          + ` réservé par un client, et ${o.perd.join(', ')} ne pourra plus le tenir`);
        if (window.confirm(
          'Planifier ce technicien rend un rendez-vous déjà réservé intenable :\n\n'
          + lignes.join('\n')
          + '\n\nIl faudra le replanifier avec le client, ou choisir un autre technicien.'
          + '\n\nPlanifier quand même ?')) {
          return doAction(action, { ...data, confirme: true });
        }
        setMsg('✗ Planification annulée');
        return;
      }
      setMsg('✗ Erreur : ' + (rep?.error || e.message));
    } finally { setSaving(''); }
  };

  const contact = () => doAction('update_contact', {
    tel_fixe: telFixe, tel_mobile: telMobile, mail_contact: mailContact, mail_contact_2: mailContact2,
  });

  const handleProposition = () => {
    if (!datePropo) return;
    const iso = heurePropo ? `${datePropo}T${heurePropo}:00` : `${datePropo}T09:00:00`;
    doAction('proposition', { date_proposition_rdv: iso, envoi_mail: envoiMail });
  };

  const handleConfirmer = () => {
    if (!dateRDV || !heureRDV || !techRDV) return;
    doAction('confirmer', {
      date_intervention: dateRDV, heure_intervention: heureRDV, technicien: techRDV,
      code_postal: itv.code_postal || null, ville: itv.ville || null,
      ref_work_order: itv.ref_work_order || null,
      commentaire_covage: itv.commentaire_covage || null,
    });
  };

  const handleSupprWO = async () => {
    // Le mail reprend les deux appels : les rappeler avant d’envoyer évite de
    // demander la suppression d’un WO dont le process n’a pas été suivi.
    const d1 = itv.date_appel_1 ? fmtDateTime(itv.date_appel_1) : 'non tracé';
    const d2 = itv.date_appel_2 ? fmtDateTime(itv.date_appel_2) : 'non tracé';
    if (!window.confirm('Demander à Covage la suppression du WO ?\n\n'
      + `Dossier : ${itv.ref_commande}\n1er appel : ${d1}\n2e appel : ${d2}\n\n`
      + 'Ces deux dates partent dans le mail.')) return;
    setSuppLoading(true);
    try {
      await demandeSuppressionWO(itv.id, itv.ref_commande);
      setSuppDone(true);
      setMsg('✓ Demande de suppression WO envoyée');
    } catch (e) {
      setMsg('✗ Erreur : ' + (e.response?.data?.error || e.message));
    } finally { setSuppLoading(false); }
  };

  // Le journal s’écrit en tête du champ Zoho, au format déjà en place : ce que
  // les autres lisent depuis Zoho ne change pas.
  const journal = entreesJournal(itv.commentaire_planif);
  const ajouterAuJournal = () => {
    const v = ligneJournal.trim();
    if (!v) return;
    const auj = new Date();
    const jj = String(auj.getDate()).padStart(2, '0') + '/' + String(auj.getMonth() + 1).padStart(2, '0');
    const texte = [(itv.commentaire_planif || '').replace(/\s+$/, ''), `${jj} : ${v}`]
      .filter(Boolean).join('\n');
    setLigneJournal('');
    doAction('commentaire_planif', { commentaire: texte });
  };

  const etat = contactStatus(itv);
  const erdv = itv.date_erdv ? fmtErdv(itv.date_erdv) : null;
  const auj = aujourdhui();
  const datePosee = String(itv.date_intervention || '').slice(0, 10);
  const dateDepassee = datePosee && datePosee < auj ? datePosee : null;
  const caches = telsCaches(itv);
  const jMail = itv.jours_ouvres_mail;
  const jCmd = daysSince(itv.date_reception);

  // La prochaine action se déduit de l’état réel. Une seule est mise en avant :
  // le volet servait à chercher quoi faire, il doit le dire.
  const SUITE = {
    aucun: {
      titre: 'Appeler le client — aucun contact tracé',
      detail: 'Le work order est arrivé, personne n’a encore appelé. Marquer le 1er appel sans '
        + 'succès le date et ouvre la suite du process.',
      libelle: 'Marquer le 1er appel sans succès', action: 'appel1',
    },
    appel1: {
      titre: 'Relancer — un seul appel tracé',
      detail: `1er appel le ${COURT(itv.date_appel_1)}. Le 2e appel conditionne la demande de `
        + 'suppression du WO à Covage.',
      libelle: 'Marquer le 2e appel sans succès', action: 'appel2',
    },
    appel2: {
      titre: 'Deux appels sans réponse — la suppression du WO est permise',
      detail: `1er appel le ${COURT(itv.date_appel_1)}, 2e le ${COURT(itv.date_appel_2)}. `
        + 'Les deux dates partent dans le mail à Covage.',
      libelle: null, action: null,
    },
    rdv_propose: {
      titre: jMail == null
        ? 'Proposition envoyée — date d’envoi non tracée'
        : `Relancer — proposition sans réponse depuis ${jMail} jour${jMail > 1 ? 's' : ''} ouvré${jMail > 1 ? 's' : ''}`,
      detail: `Proposition du ${itv.date_proposition_rdv ? fmtDateTime(itv.date_proposition_rdv) : '—'}`
        + (itv.date_envoi_mail_rdv ? `, mail parti le ${COURT(itv.date_envoi_mail_rdv)}.` : '.')
        + (itv.date_appel_2 ? '' : ' Le 2e appel n’est pas tracé, la suppression du WO n’est pas demandable.'),
      libelle: itv.date_appel_2 ? null : 'Marquer le 2e appel sans succès',
      action: itv.date_appel_2 ? null : 'appel2',
    },
  }[etat];

  const B = {
    p: { background: 'var(--blue)', color: '#fff', border: '1px solid var(--blue)' },
    s: { background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border2)' },
    d: { background: 'rgba(212,43,43,.14)', color: '#ff6b6b', border: '1px solid rgba(212,43,43,.3)' },
  };
  const bouton = (libelle, style, onClick, desactive, titre) => (
    <button onClick={onClick} disabled={!!saving || desactive} title={titre}
      style={{ font: 'inherit', fontSize: 12.5, fontWeight: 700, padding: '7px 13px', borderRadius: 8,
        cursor: desactive ? 'default' : 'pointer', whiteSpace: 'nowrap',
        opacity: (saving || desactive) ? .5 : 1, ...style }}>
      {libelle}
    </button>
  );

  const jeton = (texte, couleur, fond, titre) => (
    <span title={titre} style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 5,
      whiteSpace: 'nowrap', color: couleur, background: fond }}>{texte}</span>
  );

  const fait = (cle, valeur, petit, alerte) => (
    <div style={{ background: 'var(--bg2)', padding: '9px 11px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text3)' }}>{cle}</div>
      <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 2, color: alerte ? '#f59e0b' : 'var(--text)' }}>
        {valeur}{petit && <small style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)' }}> {petit}</small>}
      </div>
    </div>
  );

  return (
    <div>
      {/* ── Identité ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 4, background: 'var(--bg2)',
        borderBottom: '1px solid var(--border)', padding: '12px 16px 11px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.01em' }}>{itv.client_final || '—'}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text2)', fontFamily: 'var(--mono)', marginTop: 2 }}>
              {itv.ref_commande}{itv.ref_work_order && ` · ${itv.ref_work_order}`}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>
              {[itv.rue, itv.code_postal, itv.ville].filter(Boolean).join(', ')}
              {itv.client_operateur && ` · ${itv.client_operateur}`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <a href={`/dossier/${itv.id}`} target="_blank" rel="noreferrer"
              style={{ fontSize: 11.5, fontWeight: 700, padding: '5px 10px', borderRadius: 7, textDecoration: 'none',
                background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border2)', whiteSpace: 'nowrap' }}>
              Fiche ↗
            </a>
            <a href={`https://crm.zoho.eu/crm/tab/Potentials/${itv.id}`} target="_blank" rel="noreferrer"
              style={{ fontSize: 11.5, fontWeight: 700, padding: '5px 10px', borderRadius: 7, textDecoration: 'none',
                background: '#e8441a', color: '#fff', border: '1px solid #e8441a', whiteSpace: 'nowrap' }}>
              Zoho →
            </a>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 9, alignItems: 'center' }}>
          <StatutBadge itv={itv} />
          {SAV_TYPES.includes(itv.typo_raccordement)
            ? jeton('SAV', '#b45309', 'rgba(217,119,6,.18)')
            : <TypoBadge typo={itv.typo_raccordement} />}
          {itv.complexite
            ? jeton(itv.complexite, 'var(--text2)', 'var(--neutre)')
            : jeton('CX vide', '#ff6b6b', 'rgba(212,43,43,.16)', 'Complexité absente dans Zoho')}
          {itv.priorite && jeton(itv.priorite, 'var(--text2)', 'var(--neutre)')}
          <Signaux itv={itv} />
          {itv.n_via && (
            <span style={{ fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>{itv.n_via}</span>
          )}
        </div>
      </div>

      <div style={{ padding: '12px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>

        {msg && (
          <div style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            background: msg.startsWith('✓') ? '#f0fdf4' : '#fef2f2',
            color: msg.startsWith('✓') ? '#16a34a' : '#ef4444',
            border: `1px solid ${msg.startsWith('✓') ? '#bbf7d0' : '#fecaca'}` }}>{msg}</div>
        )}

        {/* ── Prochaine action ── */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)',
          borderLeft: '3px solid var(--blue2)', borderRadius: 10, padding: '13px 15px' }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--blue2)' }}>
            Prochaine action
          </div>
          <div style={{ fontSize: 14.5, fontWeight: 700, marginTop: 3 }}>{SUITE.titre}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 3, lineHeight: 1.5 }}>{SUITE.detail}</div>
          <div style={{ display: 'flex', gap: 7, marginTop: 11, flexWrap: 'wrap' }}>
            {SUITE.libelle && bouton(
              saving === SUITE.action ? '⏳…' : SUITE.libelle, B.p, () => doAction(SUITE.action))}
            {etat === 'appel2' && (suppDone
              ? <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>✓ Demande envoyée</span>
              : bouton(suppLoading ? '⏳…' : 'Suppression WO', B.d, handleSupprWO))}
            {etat !== 'appel2' && bouton('Suppression WO', B.d, handleSupprWO, !itv.date_appel_2,
              itv.date_appel_2 ? undefined : 'Il faut les deux appels tracés pour demander la suppression du WO')}
          </div>

          {/* Le moteur de créneaux, là où on décide. Deux chemins, deux
              boutons : « Proposer ce créneau » remplit les blocs du dessous
              sans rien envoyer — c'est le client qu'on n'a pas eu au
              téléphone ; « Valider ce RDV » pose le rendez-vous et bascule en
              « Planifiée » — c'est le client qui a déjà accepté. Un seul
              bouton nommé « Choisir » laissait croire que le clic envoyait la
              proposition. */}
          <PropositionDate itv={itv} dossierId={itv.id}
            onPose={() => { onRemove(itv.id); setTimeout(() => onRefresh(), 1500); }}
            onChoisir={({ date, creneau, heure: heureSav, technicien }) => {
            // Un SAV porte son heure exacte — 08:00, ou la fin du rendez-vous
            // precedent. Les creneaux de la grille, eux, se deduisent de leur
            // nom : « 09h » vaut 09:00.
            const heure = heureSav || String(creneau).replace('h', '').padStart(2, '0') + ':00';
            // La proposition sert quand le client ne repond pas au telephone,
            // la confirmation quand il a accepte : les deux chemins partent du
            // meme creneau, aucun des deux ne se retape.
            setDatePropo(date);
            setHeurePropo(heure);
            setDateRDV(date);
            setHeureRDV(heure);
            setTechRDV(technicien);
            setCreneauChoisi(n => n + 1);
            setMsg('✓ Créneau reporté dans « Proposition de RDV » et « Confirmer → Planifiée » '
              + '— rien n’est encore envoyé');
          }} />
        </div>

        {/* ── Les faits ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, background: 'var(--border)',
          border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {fait('Âge du W', itv.jours_ouvres_w == null ? '—' : itv.jours_ouvres_w,
            itv.jours_ouvres_w == null ? 'inconnu' : 'j ouvré' + (itv.jours_ouvres_w > 1 ? 's' : ''),
            horsSeuil(itv))}
          {fait('Commande', itv.date_reception ? JJMM(itv.date_reception) : '—',
            jCmd != null ? `· ${jCmd} j` : null, jCmd != null && jCmd > 60)}
          {fait('Passages', itv.nb_wo_precedents ? itv.nb_wo_precedents : '?',
            itv.nb_wo_precedents ? `· ${itv.nb_cr_rendus} CR` : 'historique incomplet',
            !itv.nb_wo_precedents || itv.nb_wo_precedents >= 3)}
          {fait('Créneau Covage', erdv ? JJMM(erdv.iso) : '—',
            erdv ? (erdv.passee ? 'périmé' : erdv.heure) : 'aucun E-RDV', erdv && erdv.passee)}
        </div>

        {/* ── Contact client ── */}
        <Section titre="Contact client" couleur={TEINTES.contact} ouvertParDefaut
          resume={[itv.nom_contact, [itv.tel_fixe, itv.tel_mobile].filter(Boolean).length + ' numéro(s)']
            .filter(Boolean).join(' · ')}>
          <InfoRow label="Nom du contact site" value={itv.nom_contact || '—'} />
          <EditableContactRow label="Tél. fixe" value={telFixe} onChange={setTelFixe} type="tel"
            link={telFixe ? `tel:${toE164(telFixe)}` : null} mono
            saving={saving === 'update_contact'} onSave={contact} />
          <EditableContactRow label="Tél. mobile" value={telMobile} onChange={setTelMobile} type="tel"
            link={telMobile ? `tel:${toE164(telMobile)}` : null} mono
            saving={saving === 'update_contact'} onSave={contact} />
          <EditableContactRow label="Mail 1" value={mailContact} onChange={setMailContact} type="email"
            link={mailContact ? `mailto:${mailContact}` : null}
            saving={saving === 'update_contact'} onSave={contact} />
          <EditableContactRow label="Mail 2" value={mailContact2} onChange={setMailContact2} type="email"
            link={mailContact2 ? `mailto:${mailContact2}` : null}
            saving={saving === 'update_contact'} onSave={contact} />
          <InfoRow label="Prise existante" value={itv.prise_existante} />

          {caches.length > 0 && (
            <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 9,
              background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.28)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>
                {caches.length === 1
                  ? 'Un numéro n’existe que dans les commentaires'
                  : `${caches.length} numéros n’existent que dans les commentaires`}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 3, lineHeight: 1.5 }}>
                Repérés dans le journal ou les notes, absents des champs du dossier : il faut les
                retrouver à la lecture, chaque fois.
              </div>
              {caches.map(c => (
                <div key={c.numero} style={{ display: 'flex', gap: 7, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                  <a href={`tel:${toE164(c.numero)}`} style={{ fontSize: 12, fontWeight: 700, padding: '4px 9px',
                    borderRadius: 7, background: 'var(--bg3)', border: '1px solid var(--border2)',
                    color: 'var(--text)', textDecoration: 'none', fontFamily: 'var(--mono)' }}>{c.affiche}</a>
                  {bouton('Ranger en mobile', B.s, () => { setTelMobile(c.numero);
                    doAction('update_contact', { tel_fixe: telFixe, tel_mobile: c.numero,
                      mail_contact: mailContact, mail_contact_2: mailContact2 }); })}
                  {bouton('Ranger en fixe', B.s, () => { setTelFixe(c.numero);
                    doAction('update_contact', { tel_fixe: c.numero, tel_mobile: telMobile,
                      mail_contact: mailContact, mail_contact_2: mailContact2 }); })}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── Accès au site : la seule consigne destinée au technicien ── */}
        {itv.commentaire_client && (
          <div style={{ padding: '11px 13px', borderRadius: 10, background: 'rgba(217,119,6,.07)',
            border: '1px solid rgba(217,119,6,.22)' }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: '#f59e0b' }}>
              Accès au site · consigne pour le technicien
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 4, whiteSpace: 'pre-wrap' }}>
              {reparerAccents(itv.commentaire_client)}
            </div>
          </div>
        )}

        {/* ── Journal ── */}
        <Section titre="Journal de planification" couleur={TEINTES.journal} ouvertParDefaut
          resume={journal.length
            ? `${journal.length} entrée${journal.length > 1 ? 's' : ''}`
              + (journal[journal.length - 1].quand ? ` · dernière le ${journal[journal.length - 1].quand}` : '')
            : 'vide'}>
          {journal.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--text3)' }}>
              Rien d’écrit sur ce dossier. La première ligne ci-dessous le commencera.
            </div>
          )}
          {journal.filter(e => e.quand).reverse().concat(journal.filter(e => !e.quand)).map((e, i) => {
            const tag = e.quand ? etiquetteJournal(e.quoi) : null;
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '58px 1fr', gap: 10, padding: '7px 0',
                borderTop: i ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: 11.5, fontWeight: e.quand ? 700 : 500, whiteSpace: 'nowrap',
                  color: e.quand ? (i === 0 ? 'var(--accent)' : 'var(--text2)') : 'var(--text3)' }}>
                  {e.quand || 'sans date'}
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.5, wordBreak: 'break-word' }}>
                  {e.quoi}
                  {tag && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                      marginLeft: 6, color: tag[1], background: tag[2], whiteSpace: 'nowrap' }}>{tag[0]}</span>
                  )}
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: 11, paddingTop: 11, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 7 }}>
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: 8,
                background: 'var(--bg3)', border: '1px solid var(--border2)', fontSize: 12, fontWeight: 700,
                color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                {String(new Date().getDate()).padStart(2, '0')}/{String(new Date().getMonth() + 1).padStart(2, '0')}
              </span>
              <input value={ligneJournal} onChange={e => setLigneJournal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') ajouterAuJournal(); }}
                placeholder="Ce qui vient de se passer…"
                style={{ flex: 1, minWidth: 0, font: 'inherit', fontSize: 12.5, padding: '8px 11px',
                  borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)' }} />
              {bouton(saving === 'commentaire_planif' ? '⏳…' : 'Ajouter', B.p, ajouterAuJournal, !ligneJournal.trim())}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {['Appel sans succès', 'Client injoignable => proposition envoyée', 'RDV validé',
                'Pas de retour --> SUPP DU WO'].map(t => (
                <button key={t} onClick={() => setLigneJournal(t)}
                  style={{ font: 'inherit', fontSize: 12, fontWeight: 600, padding: '4px 9px', borderRadius: 7,
                    background: 'var(--bg3)', border: '1px solid var(--border2)', color: 'var(--text)', cursor: 'pointer' }}>
                  {t}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.45 }}>
              La date se met toute seule et la ligne s’ajoute au champ Zoho, au format déjà en place.
            </div>
          </div>
        </Section>

        {/* ── Notes ── */}
        <Section titre="Commentaires" couleur={TEINTES.commentaires} ouvertParDefaut
          resume={[itv.commentaire_covage && 'Covage', commentaireValea && 'VALEA', commentaireInterne && 'interne']
            .filter(Boolean).join(' · ') || 'aucun'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {itv.commentaire_covage && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 4 }}>
                  Demande Covage <span style={{ fontWeight: 500 }}>— lecture seule</span>
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>
                  {reparerAccents(itv.commentaire_covage)}
                </div>
              </div>
            )}
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 4 }}>
                Contacts et notes VALEA
              </label>
              <textarea value={commentaireValea} onChange={e => setCommentaireValea(e.target.value)} rows={2}
                style={{ width: '100%', font: 'inherit', fontSize: 12.5, padding: '9px 11px', borderRadius: 8,
                  border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)',
                  resize: 'vertical', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 6 }}>
                {bouton(saving === 'commentaire_valea' ? '⏳…' : 'Enregistrer', B.s,
                  () => doAction('commentaire_valea', { commentaire: commentaireValea }),
                  commentaireValea === reparerAccents(itv.commentaire_valea || ''))}
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                  Accents réparés à la lecture.
                </span>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 4 }}>
                Note interne <span style={{ fontWeight: 500 }}>— ne sort jamais de VALEA</span>
              </label>
              <textarea value={commentaireInterne} onChange={e => setCommentaireInterne(e.target.value)} rows={2}
                style={{ width: '100%', font: 'inherit', fontSize: 12.5, padding: '9px 11px', borderRadius: 8,
                  border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)',
                  resize: 'vertical', boxSizing: 'border-box' }} />
              <div style={{ marginTop: 6 }}>
                {bouton(saving === 'commentaire_interne' ? '⏳…' : 'Enregistrer', B.s,
                  () => doAction('commentaire_interne', { commentaire: commentaireInterne }),
                  commentaireInterne === (itv.commentaire_interne || ''))}
              </div>
            </div>
          </div>
        </Section>

        {/* ── Appels ── */}
        <Section titre="Tentatives de contact" couleur={TEINTES.appels}
          resume={itv.date_appel_1
            ? `1er le ${COURT(itv.date_appel_1)}` + (itv.date_appel_2 ? ` · 2e le ${COURT(itv.date_appel_2)}` : ' · 2e non tracé')
            : 'aucun appel tracé'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[['1er appel sans succès', itv.date_appel_1, 'appel1'],
              ['2e appel sans succès', itv.date_appel_2, 'appel2']].map(([titre, quand, action]) => (
              <div key={action} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 10, padding: '9px 11px', borderRadius: 8,
                background: quand ? 'rgba(22,163,74,.08)' : 'var(--bg3)',
                border: `1px solid ${quand ? 'rgba(22,163,74,.24)' : 'var(--border)'}` }}>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: quand ? '#16a34a' : 'var(--text)' }}>{titre}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 2 }}>
                    {quand ? fmtDateTime(quand)
                      : action === 'appel2' ? 'Pas encore effectué — il ouvre la suppression du WO'
                      : 'Pas encore effectué'}
                  </div>
                </div>
                {bouton(saving === action ? '⏳…' : quand ? 'Remettre à maintenant' : 'Marquer',
                  quand ? B.s : B.p, () => doAction(action))}
              </div>
            ))}
          </div>
        </Section>

        {/* ── Proposition ── */}
        <Section titre="Proposition de RDV" couleur={TEINTES.proposition} forcerOuvert={creneauChoisi}
          resume={itv.date_proposition_rdv
            ? `${JJMM(itv.date_proposition_rdv)}${itv.envoi_mail_rdv ? ' · mail envoyé' : ' · mail non coché'}`
            : 'aucune proposition'}>
          {itv.date_proposition_rdv && (
            <div style={{ padding: '8px 11px', borderRadius: 8, background: 'rgba(37,99,235,.09)',
              border: '1px solid rgba(37,99,235,.26)', fontSize: 12.5, marginBottom: 11 }}>
              <b style={{ color: 'var(--blue2)' }}>Proposé pour le {fmtDateTime(itv.date_proposition_rdv)}</b>
              {itv.date_envoi_mail_rdv && (
                <span style={{ color: '#16a34a', fontWeight: 600 }}> · mail parti le {COURT(itv.date_envoi_mail_rdv)}</span>
              )}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
            <div>
              <label htmlFor="d-propo" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Date proposée</label>
              <input id="d-propo" type="date" value={datePropo} onChange={e => setDatePropo(e.target.value)}
                style={{ width: '100%', font: 'inherit', fontSize: 12.5, padding: '8px 10px', borderRadius: 8,
                  border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)' }} />
            </div>
            <div>
              <label htmlFor="h-propo" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Heure proposée</label>
              <input id="h-propo" type="time" value={heurePropo} onChange={e => setHeurePropo(e.target.value)}
                style={{ width: '100%', font: 'inherit', fontSize: 12.5, padding: '8px 10px', borderRadius: 8,
                  border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)' }} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, margin: '10px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={envoiMail} onChange={e => setEnvoiMail(e.target.checked)}
              style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--accent)' }} />
            <span>
              <span style={{ fontSize: 12.5, fontWeight: 600, display: 'block' }}>Envoi mail de proposition RDV</span>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                Bloque le créneau, passe l’étape en « Proposition RDV envoyée » et envoie au
                client le mail à deux boutons. Décocher rend le créneau au moteur.
              </span>
            </span>
          </label>
          {bouton(saving === 'proposition' ? '⏳…' : 'Enregistrer la proposition', B.s,
            handleProposition, !datePropo)}
        </Section>

        {/* ── Confirmation ── */}
        <Section titre="Confirmer → Planifiée" couleur={TEINTES.confirmation} forcerOuvert={creneauChoisi}
          resume={dateDepassee
            ? `date du dossier périmée (${JJMM(dateDepassee)})`
            : datePosee ? `${JJMM(datePosee)} · ${itv.technicien || 'sans technicien'}` : 'rien de saisi'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
            <div>
              <label htmlFor="d-rdv" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>
                Date d’intervention <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <input id="d-rdv" type="date" value={dateRDV} onChange={e => setDateRDV(e.target.value)}
                style={{ width: '100%', font: 'inherit', fontSize: 12.5, padding: '8px 10px', borderRadius: 8,
                  border: `1px solid ${dateRDV && dateRDV < auj ? 'rgba(212,43,43,.5)' : 'var(--border2)'}`,
                  background: 'var(--bg)', color: 'var(--text)' }} />
            </div>
            <div>
              <label htmlFor="h-rdv" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>
                Heure <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <input id="h-rdv" type="time" value={heureRDV} onChange={e => setHeureRDV(e.target.value)}
                style={{ width: '100%', font: 'inherit', fontSize: 12.5, padding: '8px 10px', borderRadius: 8,
                  border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)' }} />
            </div>
          </div>
          <div style={{ marginTop: 9 }}>
            <label htmlFor="tech-rdv" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>
              Technicien <span style={{ color: 'var(--accent)' }}>*</span>
            </label>
            <select id="tech-rdv" value={techRDV} onChange={e => setTechRDV(e.target.value)}
              style={{ width: '100%', font: 'inherit', fontSize: 12.5, padding: '8px 10px', borderRadius: 8,
                border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)' }}>
              <option value="">— Choisir un technicien —</option>
              {techniciens.filter(t => t.zoho_nom).map(t => (
                <option key={t.id} value={t.zoho_nom}>{t.prenom} {t.nom} ({t.zoho_nom})</option>
              ))}
            </select>
            {itv.technicien && (
              <div style={{ fontSize: 11, color: 'var(--blue2)', marginTop: 4 }}>
                Déjà assigné à la commande : {itv.technicien}
              </div>
            )}
          </div>
          {/* Une date passée vient du work order précédent, que l’import ne remet
              pas à zéro. Confirmer en l’état ferait annoncer au client une date
              dépassée : le serveur refuse, autant le dire ici. */}
          {dateRDV && dateRDV < auj && (
            <div style={{ marginTop: 9, padding: '9px 11px', borderRadius: 8, fontSize: 12, lineHeight: 1.5,
              background: 'rgba(212,43,43,.08)', border: '1px solid rgba(212,43,43,.26)', color: '#ff8a7a' }}>
              <b style={{ color: '#ff6b6b' }}>Cette date est passée</b> — {JJMM(dateRDV)}, restée du
              passage précédent. Confirmer ferait partir au client un mail annonçant une date
              dépassée : l’enregistrement est refusé tant qu’elle n’est pas corrigée.
            </div>
          )}
          {/* Le bouton était grisé sans dire lequel des trois champs manquait,
              et le technicien est le plus facile à oublier : il est en bas,
              et la date suffit à donner l'impression d'avoir saisi. */}
          {!techRDV && (dateRDV || heureRDV) && (
            <div style={{ marginTop: 9, padding: '9px 11px', borderRadius: 8, fontSize: 12, lineHeight: 1.5,
              background: 'rgba(212,43,43,.08)', border: '1px solid rgba(212,43,43,.26)', color: '#ff8a7a' }}>
              <b style={{ color: '#ff6b6b' }}>Aucun technicien choisi</b> — la confirmation
              partirait au client, mais l’intervention n’apparaîtrait dans aucun téléphone,
              aucune relance ne partirait le matin, et la journée resterait comptée libre : le
              créneau pourrait être revendu. Choisissez le technicien ci-dessus.
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            {bouton(saving === 'confirmer' ? '⏳ Planification…' : 'Confirmer le RDV — passer à Planifiée',
              B.p, handleConfirmer, !dateRDV || !heureRDV || !techRDV || dateRDV < auj)}
          </div>
        </Section>

        {/* ── Support Covage ── */}
        <Section titre="Support Covage" couleur={TEINTES.covage}
          resume={[[itv.nom_support_kosc, itv.prenom_support_kosc].filter(Boolean).join(' '), itv.tel_support_kosc]
            .filter(Boolean).join(' · ') || 'non renseigné'}>
          <InfoRow label="Nom" value={[itv.nom_support_kosc, itv.prenom_support_kosc].filter(Boolean).join(' ') || null} />
          <InfoRow label="Téléphone" value={itv.tel_support_kosc}
            link={itv.tel_support_kosc ? `tel:${toE164(itv.tel_support_kosc)}` : null} mono />
          <InfoRow label="Email" value={itv.mail_support_kosc}
            link={itv.mail_support_kosc ? `mailto:${itv.mail_support_kosc}` : null} />
          <InfoRow label="Service delivery" value="service.delivery@covage.com"
            link="mailto:service.delivery@covage.com" />
        </Section>

        {/* ── SLA ── */}
        {/* Les trois sorties sont celles des SLA de production. Le SAV a ses
            propres engagements, qui ne se qualifient pas ainsi : les proposer
            sur un SAV ne pouvait que tromper celui qui coche. */}
        {!SAV_TYPES.includes(itv.typo_raccordement) && <QualificationSLA itv={itv} />}

        {/* ── Étape ── */}
        <Section titre="Changer l’étape" couleur={TEINTES.etape} resume="A planifier">
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {[['Commande gelée', B.s], ['KO - Commande annulée CO', B.d], ['CR non conforme', B.s]]
              .map(([stage, style]) => bouton(
                saving === 'changer_stage' ? '⏳…' : stage, style,
                () => {
                  if (!window.confirm(`Changer l’étape vers « ${stage} » ?\n\nDossier : ${itv.ref_commande}`)) return;
                  doAction('changer_stage', {
                    stage, ref_work_order: itv.ref_work_order || null,
                    technicien: itv.technicien || null,
                    commentaire_covage: itv.commentaire_covage || null,
                  });
                }))}
          </div>
        </Section>

        {/* ── Historique ── */}
        <StageHistorySection key={itv.id} dossierId={itv.id} itv={itv} />

        <div style={{ height: 8 }} />
      </div>
    </div>
  );
}

// ─── Historique des étapes ────────────────────────────────────────────────────
function StageHistorySection({ dossierId, itv }) {
  const [history, setHistory] = React.useState(null); // null=not loaded
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const load = React.useCallback(() => {
    if (history !== null) return; // already loaded
    setLoading(true);
    api.get(`/admin/planification/${dossierId}/stage-history`)
      .then(r => setHistory(r.data.history || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [dossierId, history]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const fmtDate = iso => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const STAGE_COLORS = {
    'CLEARED': '#16a34a', 'CLEARED suite reprise': '#16a34a',
    'FAILED': '#dc2626', 'RDVNH': '#dc2626', 'CRi non cloturé': '#dc2626',
    'Planifiée': '#185FA5', 'A planifier': '#6b7280',
    'CR non conforme': '#d97706', 'Commande gelée': '#1d4ed8',
  };

  return (
    <div style={{ borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden', marginTop: 0 }}>
      <div
        onClick={toggle}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: open ? '#f8fafc' : '#fff', userSelect: 'none' }}
      >
        <span style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text2)', textTransform: 'uppercase' }}>📋 Historique des étapes</span>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 14px 12px' }}>
          {loading ? (
            <div style={{ padding: '12px 0', color: 'var(--text3)', fontSize: 12 }}>Chargement…</div>
          ) : !history || history.length === 0 ? (
            <div style={{ padding: '12px 0', color: 'var(--text3)', fontSize: 12 }}>Aucun historique disponible</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Étape', 'Date', 'RDV prévu', 'Ref WO', 'Technicien', 'Commentaire COVAGE', 'Modifié par', 'Durée'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 700, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...history].reverse().map((row, i) => {
                  const isPlannedStage = row.stage === 'Planifiée';
                  const isLatestPlanned = isPlannedStage && history.slice(0, i).every(r => r.stage !== 'Planifiée');
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '7px 8px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                          background: STAGE_COLORS[row.stage] ? STAGE_COLORS[row.stage] + '18' : '#f3f4f6',
                          color: STAGE_COLORS[row.stage] || '#374151',
                          border: `1px solid ${STAGE_COLORS[row.stage] ? STAGE_COLORS[row.stage] + '40' : '#e5e7eb'}`,
                          whiteSpace: 'nowrap',
                        }}>{row.stage}</span>
                      </td>
                      <td style={{ padding: '7px 8px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{fmtDate(row.date)}</td>
                      <td style={{ padding: '7px 8px', color: isPlannedStage ? '#185FA5' : 'var(--text3)', whiteSpace: 'nowrap', fontWeight: isPlannedStage ? 700 : 400 }}>
                        {isPlannedStage && row.date_intervention
                          ? `${row.date_intervention}${row.heure_intervention ? ' ' + row.heure_intervention : ''}`
                          : '—'}
                      </td>
                      <td style={{ padding: '7px 8px', color: 'var(--text2)', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{row.ref_work_order || '—'}</td>
                      <td style={{ padding: '7px 8px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{row.technicien || '—'}</td>
                      <td style={{ padding: '7px 8px', color: 'var(--text2)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.commentaire_covage || ''}>{row.commentaire_covage || '—'}</td>
                      <td style={{ padding: '7px 8px', color: 'var(--text2)' }}>{row.modified_by}</td>
                      <td style={{ padding: '7px 8px', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                        {row.duration_days != null ? row.duration_days + ' j' : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────
/**
 * La file des interventions à planifier.
 *
 * La page servait peu : une liste de 360 pixels où tout se ressemblait, et des
 * compteurs qui ne suivaient pas le filtre de secteur. Elle est refaite autour
 * de la question que la conduite d’activité se pose vraiment — qui reste à
 * appeler, qui attend une réponse, depuis combien de temps — en quatre files
 * de travail, un bandeau de parcours et trois alertes de veille.
 *
 * Tous les compteurs se lisent sur la population filtrée : secteur, étape,
 * recherche et veille recalculent le haut de page.
 *
 * Le panneau de détail n’a pas bougé, il passe en volet latéral : les actions
 * fines — proposition, confirmation, changement d’étape, commentaires — y
 * restent toutes. Les gestes du quotidien, marquer un appel sans succès et
 * demander la suppression d’un WO, se font désormais depuis la ligne.
 */
export default function Planification() {
  const { settings } = useSettings();
  const settingsApplied = useRef(false);
  const [interventions, setInterventions] = useState([]);
  const [techniciens, setTechniciens]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selected, setSelected]           = useState(null);
  const [relance, setRelance]             = useState(false);
  const [search, setSearch]               = useState('');
  const [secteur, setSecteur]             = useState('tous');
  const [etape, setEtape]                 = useState('tous');
  const [veille, setVeille]               = useState(null);
  const [enCours, setEnCours]             = useState(null);
  const [msg, setMsg]                     = useState('');

  const load = useCallback(async (silencieux = false) => {
    if (!silencieux) setLoading(true);
    try {
      const [planData, techData] = await Promise.all([getPlanification(), getTechniciens()]);
      setInterventions(planData.interventions || []);
      setTechniciens(techData.techniciens || []);
      setSelected(prev => prev ? (planData.interventions || []).find(i => i.id === prev.id) || null : null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (settings && !settingsApplied.current) {
      settingsApplied.current = true;
      if (settings.pages?.planification?.secteur_defaut) setSecteur(settings.pages.planification.secteur_defaut);
    }
  }, [settings]);

  // ── Filtres ──
  // Chaque compteur se lit sur la même population que la liste, à son propre
  // filtre près : l’étape 1 compte ce que le secteur et la recherche laissent
  // passer, et le sélecteur de secteur compte hors secteur.
  const passe = (itv, hors = {}) => {
    if (!hors.secteur && secteur !== 'tous' && getSecteur(itv.code_postal) !== secteur) return false;
    if (!hors.etape && etape !== 'tous' && contactStatus(itv) !== etape) return false;
    if (!hors.veille && veille) {
      if (veille === 'seuil' && !horsSeuil(itv) && !dernierJour(itv)) return false;
      if (veille === 'erdv' && !erdvPerime(itv)) return false;
      if (veille === 'attente' && contactStatus(itv) !== 'rdv_propose') return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const champs = [
        itv.ref_commande, itv.ref_work_order, itv.client_final, itv.client_operateur,
        itv.rue, itv.ville, itv.code_postal, itv.n_via,
        itv.nom_contact, itv.tel_fixe, itv.tel_mobile, itv.mail_contact, itv.mail_contact_2,
        itv.commentaire_planif, itv.commentaire_covage, itv.commentaire_interne,
        itv.commentaire_client, itv.commentaire_valea,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!champs.includes(q)) return false;
    }
    return true;
  };

  const vus         = interventions.filter(i => passe(i));
  const horsEtape   = interventions.filter(i => passe(i, { etape: true }));
  const horsVeille  = interventions.filter(i => passe(i, { veille: true }));
  const horsSecteur = interventions.filter(i => passe(i, { secteur: true }));

  // ── Veille ──
  const nHors   = horsVeille.filter(horsSeuil).length;
  const nDer    = horsVeille.filter(dernierJour).length;
  const nPerime = horsVeille.filter(erdvPerime).length;
  const nPoses  = horsVeille.filter(erdvPose).length;
  const enAttente = horsVeille.filter(i => contactStatus(i) === 'rdv_propose');
  const plusVieux = enAttente.reduce((m, i) => (i.jours_ouvres_mail != null && i.jours_ouvres_mail > m ? i.jours_ouvres_mail : m), 0);
  const sansTrace = enAttente.filter(i => i.jours_ouvres_mail == null).length;

  const ALERTES = [
    {
      k: 'seuil', n: nHors, couleur: '#b91c1c', titre: 'Hors seuil de planification',
      expl: nHors
        ? `Au-delà de ${SEUIL_OUVRE} jours ouvrés depuis l’arrivée du W, sans proposition envoyée.`
          + (nDer ? ` ${nDer} autre${nDer > 1 ? 's' : ''} au dernier jour.` : '')
        : `Tout tient dans les ${SEUIL_OUVRE} jours ouvrés` + (nDer ? ` — ${nDer} au dernier jour.` : '.'),
    },
    {
      k: 'erdv', n: nPerime, couleur: '#b45309', titre: 'E-RDV périmés',
      expl: nPerime
        ? `Sur ${nPoses} créneau${nPoses > 1 ? 'x' : ''} posé${nPoses > 1 ? 's' : ''} par Covage, `
          + 'le créneau annoncé est déjà passé : la proposition ne peut plus s’y adosser.'
        : 'Aucun créneau dépassé sur cette sélection.',
    },
    {
      k: 'attente', n: enAttente.length, couleur: '#1d4ed8', titre: 'Propositions sans réponse',
      expl: enAttente.length
        ? `Mail parti il y a ${plusVieux} jour${plusVieux > 1 ? 's' : ''} ouvré${plusVieux > 1 ? 's' : ''} au plus`
          + (sansTrace ? ` · ${sansTrace} sans date d’envoi tracée` : '') + '.'
        : 'Rien en attente côté client.',
    },
  ];

  // ── Actions depuis la ligne ──
  const ouvrir = (itv, pourRelance = false) => { setRelance(pourRelance); setSelected(itv); };

  const marquerAppel = async (itv, action) => {
    setEnCours(itv.id); setMsg('');
    try {
      await updatePlanification(itv.id, { action, dossier_ref: itv.ref_commande });
      setMsg(`✓ ${action === 'appel1' ? '1er' : '2e'} appel sans succès tracé — ${itv.ref_commande}`);
      await load(true);
    } catch (e) {
      setMsg('✗ Erreur : ' + (e.response?.data?.error || e.message));
    } finally { setEnCours(null); }
  };

  const demanderSuppression = async (itv) => {
    // Le mail reprend les deux appels : les rappeler avant d’envoyer évite de
    // demander la suppression d’un WO dont le process n’a pas été suivi.
    const d1 = itv.date_appel_1 ? fmtDateTime(itv.date_appel_1) : 'non tracé';
    const d2 = itv.date_appel_2 ? fmtDateTime(itv.date_appel_2) : 'non tracé';
    const ok = window.confirm(
      'Demander à Covage la suppression du WO ?\n\n'
      + `Dossier : ${itv.ref_commande}\n`
      + `1er appel : ${d1}\n`
      + `2e appel : ${d2}\n\n`
      + 'Ces deux dates partent dans le mail.'
    );
    if (!ok) return;
    setEnCours(itv.id); setMsg('');
    try {
      await demandeSuppressionWO(itv.id, itv.ref_commande);
      setMsg(`✓ Demande de suppression du WO envoyée — ${itv.ref_commande}`);
      await load(true);
    } catch (e) {
      setMsg('✗ Erreur : ' + (e.response?.data?.error || e.message));
    } finally { setEnCours(null); }
  };

  // Les plus en retard d’abord, et à égalité la commande la plus ancienne.
  const triFile = l => [...l].sort((a, b) =>
    ((b.jours_ouvres_w == null ? -1 : b.jours_ouvres_w) - (a.jours_ouvres_w == null ? -1 : a.jours_ouvres_w))
    || ((daysSince(b.date_reception) || 0) - (daysSince(a.date_reception) || 0)));

  const segment = (v, lbl) => {
    const n = v === 'tous' ? horsSecteur.length : horsSecteur.filter(i => getSecteur(i.code_postal) === v).length;
    return (
      <button key={v} onClick={() => setSecteur(v)} style={{
        fontSize: 13, fontWeight: 700, padding: '6px 13px', borderRadius: 6, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6, border: 'none',
        background: secteur === v ? 'rgba(24,95,165,0.14)' : 'transparent',
        color: secteur === v ? '#185FA5' : 'var(--text3)',
      }}>
        {lbl}<span style={{ fontSize: 11, fontWeight: 800, opacity: .75 }}>{n}</span>
      </button>
    );
  };

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Planification RDV</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            {loading ? 'Lecture de Zoho…' : (
              <>
                <strong style={{ color: 'var(--text)' }}>{vus.length}</strong> intervention{vus.length > 1 ? 's' : ''}
                {' '}affichée{vus.length > 1 ? 's' : ''} sur{' '}
                <strong style={{ color: 'var(--text)' }}>{interventions.length}</strong> en attente de planification
                {secteur !== 'tous' && <> · secteur {secteur}</>}
                {interventions.filter(toutJuste).length > 0 && (
                  <> · <strong style={{ color: '#16a34a' }}>
                    {interventions.filter(toutJuste).length} arrivée{interventions.filter(toutJuste).length > 1 ? 's' : ''} aujourd’hui
                  </strong></>
                )}
              </>
            )}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 9, padding: 3 }}>
            {segment('tous', 'Tous')}{segment('A', 'Secteur A')}{segment('B', 'Secteur B')}
          </div>
          <input
            placeholder="Référence, client, ville…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 232, padding: '7px 11px', border: '1px solid var(--border2)', borderRadius: 9,
              fontSize: 13, background: 'var(--bg2)', color: 'var(--text)', outline: 'none', boxSizing: 'border-box' }}
          />
          <button className="btn btn-secondary btn-sm" onClick={() => load()} disabled={loading}>
            {loading ? '⏳' : '↻ Actualiser'}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px 36px', background: 'var(--bg)' }}>

        {msg && (
          <div style={{ marginBottom: 14, padding: '9px 13px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
            background: msg.startsWith('✓') ? '#f0fdf4' : '#fef2f2',
            color: msg.startsWith('✓') ? '#16a34a' : '#ef4444',
            border: `1px solid ${msg.startsWith('✓') ? '#bbf7d0' : '#fecaca'}` }}>
            {msg}
          </div>
        )}

        {/* Bandeau de parcours — chaque étape est un filtre */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', background: 'var(--bg2)',
          border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
          {ETAPES_PARCOURS.map((e, i) => {
            const n = horsEtape.filter(itv => contactStatus(itv) === e.k).length;
            const actif = etape === e.k;
            return (
              <button key={e.k} onClick={() => setEtape(actif ? 'tous' : e.k)}
                style={{
                  textAlign: 'left', cursor: 'pointer', color: 'inherit', font: 'inherit',
                  background: actif ? 'rgba(24,95,165,0.09)' : 'transparent',
                  border: 0, borderLeft: i === 0 ? 0 : '1px solid var(--border)',
                  borderBottom: `3px solid ${actif ? 'var(--accent)' : 'transparent'}`,
                  padding: '14px 18px 16px', display: 'flex', flexDirection: 'column', gap: 2,
                }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text3)' }}>{e.rang}</span>
                <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.05,
                  color: n ? 'var(--text)' : 'var(--text3)' }}>{n}</span>
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>{e.nom}</span>
                <span style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.35 }}>{e.quoi}</span>
              </button>
            );
          })}
        </div>

        {/* Veille */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          {ALERTES.map(a => {
            const actif = veille === a.k;
            return (
              <button key={a.k} onClick={() => setVeille(actif ? null : a.k)}
                style={{
                  textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit',
                  background: 'var(--bg2)', borderRadius: 10, padding: '12px 15px',
                  border: '1px solid var(--border)', borderLeft: `3px solid ${a.couleur}`,
                  boxShadow: actif ? `0 0 0 2px ${a.couleur}40` : 'none',
                  display: 'flex', gap: 13, alignItems: 'flex-start',
                }}>
                <span style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.15,
                  minWidth: 32, color: a.n ? a.couleur : 'var(--text3)' }}>{a.n}</span>
                <span>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 1 }}>{a.titre}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text3)', lineHeight: 1.4 }}>{a.expl}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Files de travail */}
        {loading ? (
          <div style={{ padding: '50px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Chargement…</div>
        ) : vus.length === 0 ? (
          <div style={{ background: 'var(--bg2)', border: '1px dashed var(--border2)', borderRadius: 10,
            padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            {interventions.length === 0
              ? 'Aucune intervention à planifier. La file est vide.'
              : 'Aucune intervention ne correspond à ces filtres.'}
          </div>
        ) : (
          FILES_ATTENTE.filter(f => etape === 'tous' || etape === f.k).map(f => (
            <FileQueue
              key={f.k}
              file={f}
              lignes={triFile(vus.filter(i => contactStatus(i) === f.k))}
              selected={selected?.id}
              enCours={enCours}
              onOuvrir={itv => ouvrir(itv)}
              onRelancer={itv => ouvrir(itv, true)}
              onAppel={marquerAppel}
              onSupprWO={demanderSuppression}
            />
          ))
        )}

        {/* Ce que la page compte, et ce qu’elle ne compte pas */}
        <div style={{ marginTop: 26, paddingTop: 14, borderTop: '1px solid var(--border)',
          fontSize: 12, color: 'var(--text3)', lineHeight: 1.65, maxWidth: '100ch' }}>
          <strong style={{ color: 'var(--text2)' }}>Le délai qui compte est celui du W, pas celui de la commande.</strong>{' '}
          Une commande revient sous la même référence O avec un nouveau W : l’ancienneté affichée est celle du W en
          cours, la seule que Covage mesure — seuil I001/I002, {SEUIL_OUVRE} jours ouvrés entre l’arrivée du W et la
          proposition de RDV. L’âge de la commande n’apparaît qu’en rappel au-delà de deux mois : il signale un
          dossier qui tourne, pas un retard.
          <br /><br />
          <strong style={{ color: 'var(--text2)' }}>Les compteurs suivent les filtres.</strong>{' '}
          Secteur, étape, recherche et veille recalculent tout le haut de page.
        </div>
      </div>

      {/* Volet de détail — le panneau complet, ouvert sans quitter la file */}
      {selected && (
        <>
          <div onClick={() => setSelected(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.35)', zIndex: 60 }} />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(640px, 94vw)', zIndex: 61,
            background: 'var(--bg)', borderLeft: '1px solid var(--border)',
            boxShadow: '-12px 0 40px -12px rgba(15,23,42,.35)', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'var(--bg2)', flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em',
                color: 'var(--text2)', fontFamily: 'var(--mono)' }}>
                {selected.ref_commande}
              </span>
              <button onClick={() => setSelected(null)} style={{ fontSize: 12, fontWeight: 700, padding: '5px 11px',
                borderRadius: 7, background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border2)', cursor: 'pointer' }}>
                Fermer ✕
              </button>
            </div>
            {relance && (
              <div style={{ padding: '9px 14px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe',
                fontSize: 12, color: '#1d4ed8', lineHeight: 1.5, flexShrink: 0 }}>
                Proposition envoyée
                {selected.jours_ouvres_mail != null && <> il y a {selected.jours_ouvres_mail} jour{selected.jours_ouvres_mail > 1 ? 's' : ''} ouvré{selected.jours_ouvres_mail > 1 ? 's' : ''}</>}
                {' '}sans réponse. Relancez le client par téléphone, ou changez la date proposée ci-dessous — rien ne part tant que vous n’enregistrez pas.
              </div>
            )}
            <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <DetailPanel
                key={selected.id}
                itv={selected}
                techniciens={techniciens}
                onRefresh={() => load(true)}
                onRemove={id => { setInterventions(prev => prev.filter(i => i.id !== id)); setSelected(null); }}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}
